import { useCallback, useMemo } from "react";

import { Menu, Popover } from "antd";
import { clsx } from "clsx";

import type { ConfigurableVariableOption } from "../../ConfigurableVariableOption";

import styles from "./ConfigurableVariableSubMenuItem.module.scss";

export type MenuItem = {
    key: string;
    title: string;
    disabled?: boolean;
    popoverContent?: JSX.Element;
};

type ConfigurableVariableMenuItemProps = {
    item: MenuItem;
    options: Array<ConfigurableVariableOption>;
    selectedIndex: number | null;
    hasSelectedPrefix?: string;
    onClick?: (option: ConfigurableVariableOption, index: number) => void;
};

const NoItems = () => <Menu.Item disabled>No items found</Menu.Item>;

type ContentProps = {
    item: MenuItem;
    typeaheadOptions: Array<ConfigurableVariableOption>;
    selectedIndex: number | null;
    hasSelectedPrefix?: string;
    onMenuItemClick: (option: ConfigurableVariableOption, index: number) => void;
};

const SubMenuContent = ({
    item,
    typeaheadOptions,
    selectedIndex,
    hasSelectedPrefix,
    onMenuItemClick,
    ...rest
}: ContentProps) => {
    return (
        <Menu.SubMenu
            key={item.key}
            title={item.title}
            popupClassName={styles.subMenu}
            disabled={item.disabled}
            {...rest}
        >
            {typeaheadOptions.length > 0 ? (
                typeaheadOptions.map((option: ConfigurableVariableOption, index) => (
                    <Menu.Item
                        key={item.key + index}
                        className={clsx(styles.menuItem, {
                            [styles.menuItemWithDataType]: option.dataType,
                            [styles.menuItemSelected]: selectedIndex === index && hasSelectedPrefix,
                        })}
                        onClick={() => onMenuItemClick(option, index)}
                    >
                        <span>{option.displayText || option.text}</span>
                        <span>{option.nodeId ? ` (id: ${option.nodeId})` : ""}</span>
                        {option.dataType && <span className={styles.typeMetaInfo}>Data Type: {option.dataType}</span>}
                    </Menu.Item>
                ))
            ) : (
                <NoItems />
            )}
        </Menu.SubMenu>
    );
};

export function ConfigurableVariableSubMenuItem({
    item,
    options,
    selectedIndex,
    hasSelectedPrefix,
    onClick,
    ...rest
}: ConfigurableVariableMenuItemProps) {
    const typeaheadOptions = useMemo(() => {
        const regex = new RegExp(`^${item.key}(\\.|_\\d+\\.)?.*`);
        return options.filter((option) => regex.test(option.value));
    }, [item.key, options]);

    const onMenuItemClick = useCallback(
        (option: ConfigurableVariableOption, index: number) => (!!onClick ? onClick(option, index) : undefined),
        [onClick]
    );

    return item.popoverContent ? (
        <Popover key={item.key} content={item.popoverContent} trigger="hover" mouseEnterDelay={0.25}>
            <SubMenuContent
                item={item}
                typeaheadOptions={typeaheadOptions}
                selectedIndex={selectedIndex}
                hasSelectedPrefix={hasSelectedPrefix}
                onMenuItemClick={onMenuItemClick}
                {...rest}
            />
        </Popover>
    ) : (
        <SubMenuContent
            item={item}
            typeaheadOptions={typeaheadOptions}
            selectedIndex={selectedIndex}
            hasSelectedPrefix={hasSelectedPrefix}
            onMenuItemClick={onMenuItemClick}
            {...rest}
        />
    );
}
